import PropTypes from 'prop-types';
import React from 'react';
import Layout from '../../components/Layout/Layout';
import SEO from '../../components/SEO/SEO';
import Heading from '../../components/Heading/Heading';
import CTASimple from '../../components/CTASimple/CTASimple';
import CTAExpress from '../../components/CTAExpress/CTAExpress';
import theme from '../../styles/theme';
import LogoGrid from '../../components/LogoGrid/LogoGrid';
import Featurette from '../../components/Featurette/Featurette';
import Link from '../../components/Shared/Link';
import DirectLendingIcon from '../../components/Shared/Icons/DirectLending';
import PreownedHomesIcon from '../../components/Shared/Icons/PreownedHomes';
import HomeIcon from '../../components/Shared/Icons/Home';
import Posts from '../../components/Posts/Posts';
import Error from '../../components/Error/Error';
import ReactMarkdown from 'react-markdown';
import Picture from '../../components/Shared/Picture';
import { getPageContent } from '../../services/dpc';
import useGTM from '../../hooks/useGTM';
import HeroVideo from '../../components/HeroVideo/HeroVideo';
import Styles from './Homeownership.styled';
import CTACard from '../../components/CTACard/CTACard';
import EmailSignup from '../../components/Shared/EmailSignup';
import { getBaseExpressAppUrl } from '../../util/config';
import createGTMClass from '../../util/createGTMClass';
import CTACardv2 from '../../components/CTACardv2/CTACardv2';

function Homeownership({
  data: {
    contentCTAs,
    contentVideos,
    faqPod,
    heroCta,
    heroMedia,
    mobileHeroMedia,
    metaDescription,
    preFooterCta,
    tipsAndToolsPod,
    titleTag,
    pageTitle,
    customerReviewPod,
    ctaIntro,
    ctaImage,
    ctaHeader,
    webForm,
  } = {},
  error,
  isLoading,
  history,
}) {
  if (error) {
    console.error(error);
    return (
      <Layout>
        <Error />
      </Layout>
    );
  }
  const [triggerGTMEvent] = useGTM();
  const posts =
    tipsAndToolsPod &&
    tipsAndToolsPod.map(
      ({ title, contentTag, category, slug, featuredMedia }) => ({
        title,
        category,
        contentTag,
        thumbnail: `${featuredMedia?.media?.fields?.file?.url}?w=300`,
        slug,
      })
    );

  const onEmailSubmit = () => {
    triggerGTMEvent({
      name: 'vmf.cta_click',
      location: 'Home Buying',
      category: 'loan_education',
      action: 'form',
      label: 'email_sign_up_submit',
    })();
  };

  return (
    <Layout isLoading={isLoading}>
      <SEO title={titleTag} description={metaDescription} />
      <Styles>
        <Heading background={theme.colors.claytonBlue}>
          <h1 className='type-h1 title-heading'>
            {pageTitle?.slice(0, 29)}&nbsp;{pageTitle?.slice(30, 54)}
          </h1>
        </Heading>
        <HeroVideo
          configuration='homeBuying'
          title={heroCta?.ctaHeader || 'Find Your Dream Home'}
          onExpand={triggerGTMEvent({
            name: 'vmf.cta_click',
            location: 'Home Buying',
            category: 'loan_education',
            action: 'video',
            label: '60_day_no_pay_video',
          })}
          link={{
            to: heroCta?.firstCTAButtonUrl || '/',
            title: heroCta?.firstCTAButtonText || 'Apply Today',
            isExternal: true,
            newTab: true,
            onClick: triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Home Buying',
              category: 'loan_application',
              action: 'button',
              label: 'apply_now_cta',
            }),
            contentfulColors: heroCta?.buttonAndTextColors,
            className: createGTMClass('apply', 'hero', 'button'),
          }}
          thumbnail={{
            src: heroMedia?.[0]?.thumbnail?.fields?.file?.url,
            alt: heroMedia?.[0]?.thumbnail?.fields?.description,
            text: heroMedia?.[0]?.embeddedContentHeader,
          }}
          video={{
            source: heroMedia?.[0]?.videoSource,
            content: heroMedia?.[0]?.embeddedContent,
            playButtonStyle: 'centerCircle',
            onClick: triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Home Buying',
              category: 'loan_education',
              action: 'video',
              label: '60_day_no_pay_video',
            }),
          }}
          cta={
            <Link
              to={heroCta?.secondCTAButtonUrl}
              color={theme.colors.claytonBlue}
              isExternal={true}
              newTab={true}
              className={createGTMClass('find_a_home', 'hero', 'text')}
              onClick={triggerGTMEvent({
                name: 'vmf.cta_click',
                location: 'Home Buying',
                category: 'home_center',
                action: 'text',
                label: 'find_hc_text_cta',
              })}
            >
              {heroCta?.secondCTAButtonText}
            </Link>
          }
        >
          <p>{heroCta?.ctaIntro}</p>
        </HeroVideo>
        <LogoGrid title='Our Home Partners' />
        <section className='paragraph-section'>
          <Featurette
            primary={
              <Picture
                src='https://images.ctfassets.net/y64yks2od8ab/2cRK3jcUC4AAGaO38lB6Lo/6ba8e28083508eb269d22712bb77e2bf/VMFLoanGuide2023.png?w=1000'
                alt='Shop Preowned Vanderbilt Homes'
              />
            }
            secondary={
              <div className='loan-section'>
                <HomeIcon />
                <h3 className='type-h3 section-title'>Home Loan Guide</h3>
                <p className='type-p'>
                  Vanderbilt’s Home Loan Guide provides tips to help you with
                  the home loan process, budgeting, how you can use your land
                  for a down payment, credit scores, and more! We are with you
                  every step of the way and this helpful guide will prepare you
                  so that your home buying process is as easy as possible.
                </p>
                <Link
                  to='//assets.ctfassets.net/y64yks2od8ab/6vs0232K86W8EA8LTCInx5/f7527f74294262ab85080e6a073f76bb/VMF_Home_Loan_Guide.pdf'
                  isExternal={true}
                  newTab={true}
                  onClick={triggerGTMEvent({
                    name: 'vmf.cta_click',
                    location: 'Home Buying',
                    category: 'loan_education',
                    action: 'text',
                    label: 'loan_guide_text_cta',
                  })}
                >
                  View Guide
                </Link>
              </div>
            }
            center={true}
          />
        </section>
        <CTASimple
          title={contentCTAs?.[0]?.ctaHeader}
          className='mortgage-calc'
          link={{
            title: contentCTAs?.[0].firstCTAButtonText || 'Estimate Payment',
            to:
              contentCTAs?.[0]?.firstCTAButtonUrl ||
              '/calculators/mortgage-calculator/',
            size: 'large',
            onClick: triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Home Buying',
              category: 'loan_evaluation',
              action: 'button',
              label: 'mortgage_calculator_cta',
            }),
          }}
          condensed={true}
          borders={true}
        >
          <p className='type-p'>{contentCTAs?.[0]?.ctaIntro}</p>
        </CTASimple>
        <section className='paragraph-section'>
          <Featurette
            primary={
              <div>
                <DirectLendingIcon />
                <h2>Pre-owned Financing by Vanderbilt</h2>
                <p>
                  Already found your perfect manufactured home with a for sale
                  sign in the yard? Our owner-to-owner loan program can help!
                </p>
                <Link
                  to='/applynow/welcome?ReferringTM=null'
                  className={createGTMClass('apply', 'paragraph', 'text')}
                  isExternal={true}
                  newTab={true}
                  onClick={triggerGTMEvent({
                    name: 'vmf.cta_click',
                    location: 'Home Buying',
                    category: 'loan_education',
                    action: 'text',
                    label: 'direct_lending_text_cta',
                  })}
                >
                  Apply now
                </Link>
              </div>
            }
            secondary={
              <Picture
                src='https://images.ctfassets.net/y64yks2od8ab/14KpbqIYjiQ2TW839595oq/cee57c7e701fe1e019b8803bb3c70904/direct-lending.jpg?w=520'
                alt='Direct lending image'
              />
            }
            reverseOnMobile={true}
            center={true}
          />

          <Featurette
            primary={
              <Picture
                src='https://images.ctfassets.net/y64yks2od8ab/zGMexOgrhtRNpPHPhKfKU/2d245df530e429427fc20e2fa29027d7/preowned-home.jpg?w=520'
                alt='Shop Preowned Vanderbilt Homes'
              />
            }
            secondary={
              <div>
                <PreownedHomesIcon />
                <h2 className='type-h2'>Shop Preowned Vanderbilt Homes</h2>
                <p className='type-p'>
                  VMF Homes is a division of Vanderbilt Mortgage and Finance,
                  Inc., and has a wide variety of budget friendly used mobile
                  homes in inventory that are available.
                </p>
                <Link
                  to='https://www.vmfhomes.com'
                  newTab={true}
                  isExternal={true}
                  onClick={triggerGTMEvent({
                    name: 'vmf.cta_click',
                    location: 'Home Buying',
                    category: 'vmf_homes',
                    action: 'text',
                    label: 'preowned_text_cta',
                  })}
                >
                  Shop Preowned Vanderbilt Homes
                </Link>
              </div>
            }
            center={true}
          />
        </section>

        <CTAExpress
          ctaBackgroundColor={contentCTAs?.[1]?.ctaBackgroundColor
            ?.split(':')?.[1]
            ?.trim()}
          backgroundImagePath={contentCTAs?.[1]?.ctaImage?.fields?.file?.url}
          mobileBackgroundImagePath={
            contentCTAs?.[1]?.ctaMobileImage?.fields?.file?.url
          }
          subHeader={contentCTAs?.[1]?.ctaSubHeader}
          intro1={contentCTAs?.[1]?.ctaIntro?.slice(0, 51)}
          intro2={contentCTAs?.[1]?.ctaIntro?.slice(51)}
          link={{
            title: contentCTAs?.[1]?.firstCTAButtonText || 'Create account',
            to:
              contentCTAs?.[1]?.firstCTAButtonUrl ||
              'https://www.vmf.com/portal/onboarding',
            onClick: triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Home Buying',
              category: 'create_account',
              action: 'button',
              label: 'express_lending_create_account',
            }),
            className: createGTMClass('create_account', 'cta', 'button'),
          }}
          secondaryLink={{
            title: contentCTAs?.[1]?.secondCTAButtonText || 'Sign in',
            to:
              contentCTAs?.[1]?.secondCTAButtonUrl ||
              getBaseExpressAppUrl() + '/portal/login',
            onClick: triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Home Buying',
              category: 'account_sign_in',
              action: 'button',
              label: 'express_lending_sign_in',
            }),
            className: createGTMClass('sign_in', 'cta', 'button'),
          }}
        />

        <Posts
          title='First-Time Home Buyer Tips'
          className='home-buying-posts'
          link={{
            title: 'View all',
            to: '/resources/blog/',
            onClick: triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Home Buying',
              category: 'loan_education',
              action: 'text',
              label: 'buying_tips_text_cta',
            }),
          }}
          posts={posts}
          configuration='homeBuying'
        />
        <CTACard
          animationThreshold={0.2}
          backgroundBottom={theme.colors.white}
          className='email-subscribe'
          flat={true}
          form={
            <EmailSignup
              buttonLabel={webForm?.[0]?.firstFormButtonText}
              originCampaign={webForm?.[0]?.originCampaign}
              originWebApp={webForm?.[0]?.originWebApp}
              originWebFormCategory={webForm?.[0]?.originWebFormCategory}
              originWebFormType={webForm?.[0]?.originWebFormType}
              originWebPageType={webForm?.[0]?.originWebPageType}
              dataExtension={webForm?.[0]?.dataExtension}
              onEmailSubmit={onEmailSubmit}
              className={createGTMClass('subscribe', 'cta', 'button')}
            />
          }
          disclaimer={
            <ReactMarkdown
              source={webForm?.[0]?.disclaimer}
              renderers={{
                paragraph: ({ children }) => (
                  <p className='type-caption bold color-grey1'>{children}</p>
                ),
              }}
            />
          }
          divider={false}
        >
          <p className='type-label-large bold'>{webForm?.[0]?.formIntro}</p>
          <h3 className='type-h1'>{webForm?.[0]?.formHeader}</h3>
        </CTACard>
        <CTACardv2
          preHeaderContent={'Have Questions?'}
          headerContent={`We're Here to Help`}
          buttonText={'Contact Us'}
          buttonUrl={'/contact-us/'}
          gtmClassName={'contact'}
          backgroundBottom={theme.colors.white}
          ctaIntro={
            <>
              <p>
                Existing customers{' '}
                <a href='tel:1-800-970-7250'>1-800-970-7250</a>
              </p>
              <p>
                New customers <a href='tel:1-866-701-0467'>1-866-701-0467</a>
              </p>
            </>
          }
        />
        <CTASimple
          title={
            preFooterCta?.ctaHeader || 'Find the Perfect Home Loan for You'
          }
          link={{
            to: preFooterCta?.firstCTAButtonUrl || '/applynow/welcome/',
            title: preFooterCta?.firstCTAButtonText || 'Apply Today',
            isExternal: true,
            newTab: true,
            size: 'large',
            onClick: triggerGTMEvent({
              name: 'vmf.cta_click',
              location: 'Home Buying',
              category: 'loan_application',
              action: 'button',
              label: 'apply_today_cta',
            }),
            contentfulColors: preFooterCta?.buttonAndTextColors,
            className: createGTMClass('apply', 'prefoot_cta', 'button'),
          }}
          background={
            preFooterCta?.ctaBackgroundColor?.split(':')?.[1]?.trim() ||
            theme.colors.claytonBlue
          }
        />
      </Styles>
    </Layout>
  );
}

Homeownership.getInitialProps = async ({}) => {
  try {
    const data = await getPageContent('/home-buying/');

    if (data.status !== 200) {
      throw data.statusText;
    }

    return {
      data: data.data.result,
    };
  } catch (error) {
    return {
      error,
    };
  }
};

Homeownership.propTypes = {
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  data: PropTypes.shape({}),
  error: PropTypes.object,
};

export default Homeownership;
